import type { TicketOrderListItem, TicketOrderStatus, TicketStatus } from '@pulsify/shared/dto'
import { Check, UserRound } from 'lucide-react'
import * as m from '~/paraglide/messages'
import { initialsOf } from '~/shared/lib/initials'
import { cn } from '~/shared/lib/utils'
import { Avatar, AvatarFallback } from '~/shared/ui/avatar'
import { type EventPhase, entryOf } from '../lib/ticket-figures'

const ORDER_STATUS: Record<TicketOrderStatus, () => string> = {
  paid: m.ticketsOrderPaid,
  free: m.ticketsOrderFree,
  refunded: m.ticketsOrderRefunded,
  pending: m.ticketsOrderPending,
  expired: m.ticketsOrderExpired,
}

const TICKET_STATUS: Record<TicketStatus, () => string> = {
  active: m.ticketsTicketActive,
  void: m.ticketsTicketVoid,
  refunded: m.ticketsTicketRefunded,
}

const CHIP =
  'inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 font-medium text-xs'

export function OrderStatusChip({ status }: { status: TicketOrderStatus }) {
  return (
    <span
      className={cn(
        CHIP,
        status === 'paid' ? 'bg-foreground/[0.07] text-foreground' : 'border',
        status === 'free' && 'border-foreground/15 text-foreground',
        (status === 'refunded' || status === 'expired' || status === 'pending') &&
          'border-dashed border-foreground/25 text-muted-foreground',
      )}
    >
      {ORDER_STATUS[status]()}
    </span>
  )
}

export function TicketStatusChip({ status }: { status: TicketStatus }) {
  return (
    <span className={cn(CHIP, 'border border-dashed border-foreground/25 text-muted-foreground')}>
      {TICKET_STATUS[status]()}
    </span>
  )
}

const PHASE_LABEL: Record<EventPhase, () => string> = {
  upcoming: m.ticketPagePhaseUpcoming,
  live: m.ticketPagePhaseLive,
  past: m.ticketPagePhasePast,
  canceled: m.canceled,
}

export function PhaseChip({ phase }: { phase: EventPhase }) {
  return (
    <span
      className={cn(
        CHIP,
        phase === 'upcoming' && 'border border-foreground/15 text-foreground',
        phase === 'live' && 'bg-brand-subtle text-brand-subtle-foreground',
        phase === 'past' && 'bg-foreground/[0.07] text-muted-foreground',
        phase === 'canceled' && 'border border-destructive/40 text-destructive',
      )}
    >
      {phase === 'live' ? <span aria-hidden className="size-1.5 rounded-full bg-brand" /> : null}
      {PHASE_LABEL[phase]()}
    </span>
  )
}

export function BuyerAvatar({ name, className }: { name: string | null; className?: string }) {
  return (
    <Avatar className={cn('size-8', className)}>
      <AvatarFallback className="font-medium text-[11px] text-muted-foreground tracking-wide">
        {name ? initialsOf(name) : <UserRound aria-hidden className="size-4" />}
      </AvatarFallback>
    </Avatar>
  )
}

export function BuyerName({ name, className }: { name: string | null; className?: string }) {
  return name ? (
    <span className={cn('truncate font-medium text-foreground', className)}>{name}</span>
  ) : (
    <span className={cn('truncate text-muted-foreground italic', className)}>
      {m.ticketsNoName()}
    </span>
  )
}

export function EntryCount({ order }: { order: Pick<TicketOrderListItem, 'tickets'> }) {
  const { checkedIn, valid } = entryOf(order)
  if (valid === 0) return <span className="text-muted-foreground">-</span>
  const complete = checkedIn === valid
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 tabular-nums',
        checkedIn === 0 ? 'text-muted-foreground' : 'text-foreground',
      )}
      title={m.ticketPageEntryLabel({ done: String(checkedIn), total: String(valid) })}
    >
      <span className="sr-only">
        {m.ticketPageEntryLabel({ done: String(checkedIn), total: String(valid) })}
      </span>
      <span aria-hidden>
        {checkedIn}/{valid}
      </span>
      {complete ? <Check aria-hidden className="size-3.5 text-brand" strokeWidth={2.5} /> : null}
    </span>
  )
}

export function ProgressBar({
  ratio,
  label,
  accent = false,
}: {
  ratio: number
  label: string
  accent?: boolean
}) {
  const percent = Math.round(ratio * 100)
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      className="h-1 w-full overflow-hidden rounded-full bg-foreground/[0.08]"
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width] duration-500 ease-spring motion-reduce:transition-none',
          accent ? 'bg-brand' : 'bg-foreground/70',
        )}
        style={{ width: `${percent}%` }}
      />
    </div>
  )
}
